import {
  PriceDiscoveryVersion,
  WLStripeEvent,
} from "../../Common/models/subscriptions";
import {
  StripeCustomerExpress,
  UserWebCreate2,
} from "../../Common/models/user";
import { doGet, doPost, encodeQueryData } from "../../Common/utils/https";
import { getRequestHeaders } from "../../Common/utils/https";
import { cloudFunctionsUrl } from "../firebase";
import { AxiosResponse } from "axios";
import Stripe from "stripe";
const baseUrl = cloudFunctionsUrl;

export const getStripeSubscriptions = async (
  alternativePriceVersion?: PriceDiscoveryVersion
) => {
  const url =
    alternativePriceVersion === undefined
      ? `${baseUrl}stripe/getSubscriptions`
      : `${baseUrl}stripe/getSubscriptions?${encodeQueryData({
          alternativePriceVersion,
        })}`;
  const headers = await getRequestHeaders();
  return doGet(url, headers);
};

export const validateStripePromo = async ({
  promoCode,
}: {
  promoCode: string;
}) => {
  const url = `${baseUrl}stripe/validatePromo?${encodeQueryData({
    promoCode,
  })}`;

  const headers = await getRequestHeaders();
  return doGet(url, headers);
};

interface CreateStripeSubscriptionSinglePage extends UserWebCreate2 {
  priceId: string;
  couponId: string;
  promoCode: string;
  referralBonusLookUpKey: null | string;
  signupPreferenceProperties?: Record<string, string> | null;
}
export const createSubscriptionSinglePage = async (
  params: CreateStripeSubscriptionSinglePage
) => {
  const url = `${baseUrl}stripe/createSubscriptionSinglePage`;
  const headers = await getRequestHeaders();
  return doPost(url, headers, {
    ...params,
  });
};

interface CreateCustomerExpressCheckout extends StripeCustomerExpress {
  emailAddress: string;
  phoneNumber: string;
  priceId: string;
  couponId: string;
  promoCode: string;
  referralBonusLookUpKey: null | string;
  signupPreferenceProperties?: Record<string, string> | null;
}

export const createSubscriptionExpressCheckout = async (
  params: CreateCustomerExpressCheckout
) => {
  const url = `${baseUrl}stripe/createSubscriptionExpressCheckout`;
  const headers = await getRequestHeaders();
  return doPost(url, headers, {
    ...params,
  });
};

interface CreateOneOffSubscriptionSinglePage extends UserWebCreate2 {
  priceId: string;
  wlSubscriptionId: string;
  couponId: string;
  promoCode: string;
  giveawayId: string;
  doubleForHalfOffAvailed?: boolean;
}

export const createOneOffSubscriptionSinglePage = async (
  params: CreateOneOffSubscriptionSinglePage
) => {
  const url = `${baseUrl}stripe/createOneOffSubscriptionSinglePage`;
  const headers = await getRequestHeaders();
  return doPost(url, headers, {
    ...params,
  });
};

export const createOneOffExpressCheckout = async (
  params: CreateOneOffSubscriptionSinglePage
) => {
  const url = `${baseUrl}stripe/createOneOffExpressCheckout`;
  const headers = await getRequestHeaders();
  return doPost(url, headers, {
    ...params,
  });
};

export const getActiveSubscription = async (customerId: string) => {
  const url = `${baseUrl}stripe/getActiveSusbcription?${encodeQueryData({
    customerId,
  })}`;

  const headers = await getRequestHeaders(true);
  return doGet(url, headers);
};

export const getUserLatestFailedPaymentAttempt = async (
  userId: string
): Promise<AxiosResponse<{ result: Stripe.Event }>> => {
  const url = `${baseUrl}stripe/getUserLatestFailedPaymentAttempt?${encodeQueryData(
    {
      userId,
    }
  )}`;

  const headers = await getRequestHeaders(true);
  return doGet(url, headers);
};

export const setDefaultPayment = async ({
  customerId,
  paymentMethodId,
  subscriptionId,
}: {
  customerId: string;
  paymentMethodId: string;
  subscriptionId: string;
}) => {
  const url = `${baseUrl}stripe/setDefaultPayment`;
  const headers = await getRequestHeaders(true);
  return doPost(url, headers, {
    customerId,
    paymentMethodId,
    subscriptionId,
  });
};

export const removePaymentMethod = async ({
  paymentMethodId,
}: {
  paymentMethodId: string;
}) => {
  const url = `${baseUrl}stripe/removePaymentMethod`;
  const headers = await getRequestHeaders(true);
  return doPost(url, headers, {
    paymentMethodId,
  });
};

export const getActiveCustomer = async (customerId: string) => {
  const url = `${baseUrl}stripe/getActiveCustomer?${encodeQueryData({
    customerId,
  })}`;

  const headers = await getRequestHeaders(true);
  return doGet(url, headers);
};

export const getUserPaymentMethods = async (customerId: string) => {
  const url = `${baseUrl}stripe/getUserPaymentMethods?${encodeQueryData({
    customerId,
  })}`;

  const headers = await getRequestHeaders(true);
  return doGet(url, headers);
};

export const addPaymentMethod = async ({
  customerId,
  paymentMethodId,
  isDefault,
}: {
  customerId: string;
  paymentMethodId: string;
  isDefault: boolean;
}) => {
  const url = `${baseUrl}stripe/addPaymentMethod`;
  const headers = await getRequestHeaders(true);
  return doPost(url, headers, {
    customerId,
    paymentMethodId,
    isDefault,
  });
};

export const pauseSubscription = async (stripeData: {
  stripeCustomerId: string;
  subscriptionId: string;
}) => {
  const url = `${baseUrl}stripe/pauseSubscription`;
  const headers = await getRequestHeaders(true);
  return doPost(url, headers, { ...stripeData });
};

export const updateStripeSubscription = async (stripeData: {
  customerId: string;
  priceId: string;
  prorated: boolean;
  paymentMethodId?: string;
}) => {
  const url = `${baseUrl}stripe/updatePlan`;
  const headers = await getRequestHeaders(true);
  return doPost(url, headers, { ...stripeData });
};

export const createAndApplyDiscount = async (data: {
  subscriptionId: string;
}) => {
  const url = `${baseUrl}stripe/createAndApplyDiscountForCancellingUser`;
  const headers = await getRequestHeaders(true);
  return doPost(url, headers, data);
};

export const extendSubscriptionForCancellingUser = async (data: {
  subscriptionId: string;
}) => {
  const url = `${baseUrl}stripe/extendSubscriptionForCancellingUser`;
  const headers = await getRequestHeaders(true);
  return doPost(url, headers, data);
};

export const cancelStripeSubscription = async ({
  subscriptionId,
  cancelImmediately,
  reason,
  otherRemarks,
}: {
  subscriptionId: string;
  cancelImmediately: boolean;
  reason: string;
  otherRemarks: string;
}) => {
  const url = `${baseUrl}stripe/cancelSubscription`;
  const headers = await getRequestHeaders(true);
  return doPost(url, headers, {
    subscriptionId,
    cancelImmediately,
    reason,
    otherRemarks,
  });
};

export const retryPayment = async ({
  customerId,
  paymentMethodId,
  invoiceId,
  subscriptionId,
  couponId,
  promoCode,
  offerViewedAt,
}: {
  customerId: string;
  paymentMethodId: string;
  invoiceId: string;
  subscriptionId: string;
  couponId: string;
  promoCode: string;
  offerViewedAt?: "app" | "web" | "redirectFromApp" | "drawDayWebFlow" | "admin";
}) => {
  const url = `${baseUrl}stripe/retryPayment`;
  const headers = await getRequestHeaders(true);
  return doPost(url, headers, {
    customerId,
    paymentMethodId,
    invoiceId,
    subscriptionId,
    couponId,
    promoCode,
    ...(offerViewedAt && { offerViewedAt }),
  });
};

export const retryPaymentWithStripeForm = async ({
  customerId,
  subscriptionId,
  couponId,
  promoCode,
  offerViewedAt,
}: {
  customerId: string;
  subscriptionId: string;
  couponId: string;
  promoCode: string;
  offerViewedAt?: "app" | "web" | "redirectFromApp" | "drawDayWebFlow" | "admin";
}) => {
  const url = `${baseUrl}stripe/retryPaymentWithStripeForm`;
  const headers = await getRequestHeaders(true);
  return doPost(url, headers, {
    customerId,
    subscriptionId,
    couponId,
    promoCode,
    ...(offerViewedAt && { offerViewedAt }),
  });
};
